import { askConfirm } from '@/components/ui/ConfirmHost';
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getGradingScales, postGradingScale, putGradingScale, deleteGradingScale,
  getAssessmentComponents, postAssessmentComponent, putAssessmentComponent, deleteAssessmentComponent, getClasses,
} from '@/api/owner';
import { Plus, X, Edit2, Trash2, AlertCircle, CheckCircle2, Scale, Hash, Users, Search } from 'lucide-react';

/** "All classes" or a chosen list of classes. */
function AppliesTo({ value, onChange, classes, note, startSome }: { value: number[]; onChange: (v: number[]) => void; classes: { id: number; label: string }[]; note: string; startSome?: boolean }) {
  const [mode, setMode] = useState<'all' | 'some'>(value.length || startSome ? 'some' : 'all');
  return (
    <div>
      <label className="block text-sm font-semibold text-slate-700 mb-1">Applies to</label>
      <div className="inline-flex bg-slate-100 rounded-lg p-1 w-full">
        <button type="button" onClick={() => { setMode('all'); onChange([]); }} className={`flex-1 px-3 py-1.5 rounded-md text-xs font-semibold ${mode === 'all' ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}>All classes</button>
        <button type="button" onClick={() => setMode('some')} className={`flex-1 px-3 py-1.5 rounded-md text-xs font-semibold ${mode === 'some' ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}>Selected classes</button>
      </div>
      {mode === 'some' && (
        <div className="mt-2 flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
          {classes.map((c) => { const on = value.includes(c.id); return (
            <button type="button" key={c.id} onClick={() => onChange(on ? value.filter((x) => x !== c.id) : [...value, c.id])}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${on ? 'bg-[#0f172a] text-white border-[#0f172a]' : 'border-slate-300 text-slate-600 hover:border-slate-500'}`}>{c.label}</button>); })}
          {!classes.length && <span className="text-xs text-slate-500">No classes yet.</span>}
        </div>
      )}
      <p className="text-[11px] text-slate-500 mt-1">{mode === 'all' ? note : value.length ? `${value.length} class(es) chosen. Other classes keep using the all-classes setting.` : 'Pick at least one class.'}</p>
    </div>
  );
}

const arr = <T,>(src: any): T[] => {
  if (!src) return [];
  if (Array.isArray(src)) return src;
  if (Array.isArray(src.data)) return src.data;
  if (Array.isArray(src.data?.data)) return src.data.data;
  return [];
};

interface GradeItem {
  grade: string;
  min_score: string;
  max_score: string;
  remark: string;
  gpa_point: string;
}

interface ScaleForm {
  id?: number;
  name: string;
  is_default: boolean;
  class_ids: number[];
  items: GradeItem[];
}

interface ColumnForm {
  id?: number;
  name: string;
  code: string;
  max_score: string;
  kind: 'ca' | 'test' | 'exam' | 'project' | 'other';
  position: string;
  class_ids: number[];
}

const emptyScale: ScaleForm = {
  name: '',
  is_default: false,
  class_ids: [],
  items: [
    { grade: 'A1', min_score: '75', max_score: '100', remark: 'Excellent', gpa_point: '' },
    { grade: 'B2', min_score: '70', max_score: '74', remark: 'Very good', gpa_point: '' },
    { grade: 'B3', min_score: '65', max_score: '69', remark: 'Good', gpa_point: '' },
    { grade: 'C4', min_score: '60', max_score: '64', remark: 'Credit', gpa_point: '' },
    { grade: 'C5', min_score: '55', max_score: '59', remark: 'Credit', gpa_point: '' },
    { grade: 'C6', min_score: '50', max_score: '54', remark: 'Credit', gpa_point: '' },
    { grade: 'D7', min_score: '45', max_score: '49', remark: 'Pass', gpa_point: '' },
    { grade: 'E8', min_score: '40', max_score: '44', remark: 'Pass', gpa_point: '' },
    { grade: 'F9', min_score: '0',  max_score: '39', remark: 'Fail',   gpa_point: '' },
  ],
};

const emptyColumn: ColumnForm = {
  name: '', code: '', max_score: '', kind: 'ca', position: '', class_ids: [],
};

export function GradingPage() {
  const qc = useQueryClient();
  const [scaleForm, setScaleForm] = useState<ScaleForm | null>(null);
  const [columnForm, setColumnForm] = useState<ColumnForm | null>(null);
  const [error, setError] = useState<string | null>(null);

  const scalesQ = useQuery({
    queryKey: ['grading-scales'],
    queryFn: () => getGradingScales().then((r: any) => r.data?.data ?? r.data),
  });
  const componentsQ = useQuery({
    queryKey: ['assessment-components'],
    queryFn: () => getAssessmentComponents().then((r: any) => r.data?.data ?? r.data),
  });

  const classesQ = useQuery({ queryKey: ['owner-classes'], queryFn: () => getClasses({ per_page: 200 }).then((r: any) => r.data?.data ?? r.data) });
  const classes = arr<any>(classesQ.data).map((c: any) => ({ id: Number(c.id), label: [c.name, c.arm].filter(Boolean).join(' ') }));
  const scales = arr<any>(scalesQ.data);
  const componentsRaw = componentsQ.data;
  const allComponents = arr<any>(componentsRaw?.components ?? componentsRaw);
  const sets: { class_ids: number[]; classes: string[]; component_ids: number[]; total_max: number }[] = componentsRaw?.class_sets ?? [];
  const [setIdx, setSetIdx] = useState(-1); // -1 = all classes
  const activeSet = setIdx >= 0 ? sets[setIdx] : null;
  const components = activeSet ? allComponents.filter((c: any) => activeSet.component_ids.includes(c.id)) : allComponents.filter((c: any) => !(c.class_ids ?? []).length);
  const totalMax = components.filter((c: any) => !c.class_level_id).reduce((s: number, c: any) => s + Number(c.max_score || 0), 0);
  // which columns and scale a class ends up with
  const [peek, setPeek] = useState<number | ''>('');
  const peekQ = useQuery({ queryKey: ['assessment-components', 'class', peek], enabled: !!peek, queryFn: () => getAssessmentComponents({ class_id: peek }).then((r: any) => r.data?.data ?? r.data) });
  const peekScale = peek ? (scales.find((x: any) => (x.class_ids ?? []).includes(Number(peek))) ?? scales.find((x: any) => x.is_default)) : null;

  // ─── mutations ───
  const saveScaleM = useMutation({
    mutationFn: (body: any) => scaleForm?.id
      ? putGradingScale(scaleForm.id, body)
      : postGradingScale(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['grading-scales'] }); closeScaleForm(); },
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Save failed'),
  });

  const deleteScaleM = useMutation({
    mutationFn: (id: number) => deleteGradingScale(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['grading-scales'] }),
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Delete failed'),
  });

  const saveColumnM = useMutation({
    mutationFn: (body: any) => columnForm?.id
      ? putAssessmentComponent(columnForm.id, body)
      : postAssessmentComponent(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['assessment-components'] }); closeColumnForm(); },
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Save failed'),
  });

  const deleteColumnM = useMutation({
    mutationFn: (id: number) => deleteAssessmentComponent(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assessment-components'] }),
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Delete failed'),
  });

  // ─── scale form helpers ───
  const closeScaleForm = () => { setScaleForm(null); setError(null); };
  const openNewScale = () => { setScaleForm({ ...emptyScale }); setError(null); };
  const openEditScale = (s: any) => {
    setScaleForm({
      id: s.id,
      name: s.name ?? '',
      is_default: !!s.is_default,
      class_ids: (s.class_ids ?? []).map(Number),
      items: (s.items ?? []).map((i: any) => ({
        grade: i.grade ?? '',
        min_score: String(i.min_score ?? ''),
        max_score: String(i.max_score ?? ''),
        remark: i.remark ?? '',
        gpa_point: i.gpa_point != null ? String(i.gpa_point) : '',
      })),
    });
    setError(null);
  };

  const updateGradeItem = (idx: number, patch: Partial<GradeItem>) => {
    if (!scaleForm) return;
    setScaleForm({
      ...scaleForm,
      items: scaleForm.items.map((it, i) => i === idx ? { ...it, ...patch } : it),
    });
  };

  const addGradeItem = () => {
    if (!scaleForm) return;
    setScaleForm({ ...scaleForm, items: [...scaleForm.items, { grade: '', min_score: '', max_score: '', remark: '', gpa_point: '' }] });
  };
  const removeGradeItem = (idx: number) => {
    if (!scaleForm) return;
    setScaleForm({ ...scaleForm, items: scaleForm.items.filter((_, i) => i !== idx) });
  };

  const validateAndSaveScale = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!scaleForm) return;
    if (!scaleForm.name.trim()) { setError('Name is required.'); return; }
    if (scaleForm.items.length === 0) { setError('Add at least one grade band.'); return; }

    // Validate ranges
    const items = scaleForm.items.map(i => ({
      grade: i.grade.trim().toUpperCase(),
      min: Number(i.min_score),
      max: Number(i.max_score),
      remark: i.remark.trim(),
      gpa: i.gpa_point ? Number(i.gpa_point) : undefined,
    }));

    if (items.some(i => !i.grade)) { setError('Every band needs a grade (A1, B2, etc).'); return; }
    if (items.some(i => !Number.isFinite(i.min) || !Number.isFinite(i.max))) { setError('Every band needs a numeric min and max.'); return; }
    if (items.some(i => i.min > i.max)) { setError('A band has min greater than max.'); return; }
    if (items.some(i => i.min < 0 || i.max > 100)) { setError('All scores must be between 0 and 100.'); return; }

    // Check for overlaps
    const sorted = [...items].sort((a, b) => a.min - b.min);
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].min <= sorted[i - 1].max) {
        setError(`Bands overlap: ${sorted[i-1].grade} (${sorted[i-1].min}-${sorted[i-1].max}) and ${sorted[i].grade} (${sorted[i].min}-${sorted[i].max}).`);
        return;
      }
    }

    if (!scaleForm.is_default && scaleForm.class_ids.length === 0 && scales.some((x: any) => x.is_default && x.id !== scaleForm.id)) {
      // a second all-classes scale is only useful as the default
    }
    saveScaleM.mutate({
      name: scaleForm.name.trim(),
      is_default: scaleForm.class_ids.length ? false : scaleForm.is_default,
      class_ids: scaleForm.class_ids,
      items: items.map(i => ({
        grade: i.grade,
        min_score: i.min,
        max_score: i.max,
        remark: i.remark || null,
        gpa_point: i.gpa ?? null,
      })),
    });
  };

  // ─── column form helpers ───
  const closeColumnForm = () => { setColumnForm(null); setForSome(false); setError(null); };
  const openNewColumn = () => { setColumnForm({ ...emptyColumn, class_ids: activeSet ? [...activeSet.class_ids] : [] }); setError(null); };
  const openNewClassSet = () => { setColumnForm({ ...emptyColumn, class_ids: [] , code: '', name: '' }); setForSome(true); setError(null); };
  const [forSome, setForSome] = useState(false);
  const openEditColumn = (c: any) => {
    setColumnForm({
      id: c.id,
      name: c.name ?? '',
      code: c.code ?? '',
      max_score: String(c.max_score ?? ''),
      kind: c.kind ?? 'ca',
      position: c.position != null ? String(c.position) : '',
      class_ids: (c.class_ids ?? []).map(Number),
    });
    setError(null);
  };

  const saveColumn = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!columnForm) return;
    if (!columnForm.name.trim()) { setError('Name is required.'); return; }
    if (!columnForm.code.trim()) { setError('Code is required (e.g. CA1, EXAM).'); return; }
    if (!columnForm.max_score || Number(columnForm.max_score) <= 0) { setError('Max score must be greater than 0.'); return; }

    saveColumnM.mutate({
      name: columnForm.name.trim(),
      code: columnForm.code.trim().toUpperCase(),
      max_score: Number(columnForm.max_score),
      kind: columnForm.kind,
      position: columnForm.position ? Number(columnForm.position) : undefined,
      class_ids: columnForm.class_ids,
    });
  };

  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-6">
      <div>
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Academics</p>
        <h1 className="text-3xl font-extrabold text-slate-900">Grading &amp; Weights</h1>
        <p className="text-slate-500 text-sm mt-1">Grade scale + how CA and Exam combine into a total. Set them for all classes, or give some classes their own.</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="flex items-start gap-3 flex-1">
          <span className="w-9 h-9 rounded-lg bg-slate-100 grid place-items-center shrink-0"><Search className="w-4 h-4 text-slate-600" /></span>
          <div><p className="text-sm font-bold text-slate-900">What does a class use?</p>
            <p className="text-xs text-slate-500">A class uses the columns and scale set for it; if none, the ones for all classes. Score entry, results, broadsheets, report sheets and transcripts all follow this.</p></div>
        </div>
        <select value={peek} onChange={(e) => setPeek(e.target.value ? Number(e.target.value) : '')} className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white lg:w-56">
          <option value="">Choose a class…</option>
          {classes.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </div>
      {peek && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900 flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="flex-1"><b>{classes.find((c) => c.id === peek)?.label}</b> enters scores in{' '}
            {peekQ.isLoading ? '…' : <>{arr<any>(peekQ.data?.components).map((c: any) => `${c.code} (${c.max_score})`).join(' · ') || 'no columns yet'}{' '}
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white border border-blue-200">{peekQ.data?.scope === 'class' ? 'its own columns' : 'all-classes columns'}</span></>}
            {' '}and grades with <b>{peekScale?.name ?? '—'}</b>{peekScale && !(peekScale.class_ids ?? []).includes(Number(peek)) ? ' (default)' : ''}.</div>
          <button onClick={() => setPeek('')} className="text-xs font-semibold text-blue-800">Close</button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* ═══════════════ GRADE SCALES ═══════════════ */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Scale className="w-4 h-4 text-slate-500" />
              <h2 className="font-bold text-slate-900">Grade scale</h2>
            </div>
            <button onClick={openNewScale}
              className="text-xs font-bold bg-[#0f172a] text-white px-3 py-1.5 rounded-lg flex items-center gap-1">
              <Plus className="w-3 h-3" /> New
            </button>
          </div>

          {scalesQ.isLoading ? <p className="p-6 text-center text-sm text-slate-500">Loading…</p> :
           scales.length === 0 ? (
            <p className="p-6 text-center text-sm text-slate-500">No grade scale yet.</p>
           ) : (
            <div className="divide-y divide-slate-100">
              {scales.map((s: any) => (
                <div key={s.id} className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-sm font-bold text-slate-900">
                        {s.name}
                        {s.is_default && (
                          <span className="ml-2 text-[10px] font-bold uppercase bg-green-50 text-green-700 border border-green-200 rounded-full px-2 py-0.5">Default</span>
                        )}
                      </p>
                      <p className="text-xs text-slate-500">{s.items?.length ?? 0} bands · {(s.classes ?? []).length ? <span className="text-slate-700 font-semibold">{s.classes.join(', ')}</span> : s.is_default ? 'all classes without their own scale' : <span className="text-amber-700">not used — choose classes for it</span>}</p>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => openEditScale(s)} className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded">
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={async () => { if (await askConfirm(`Delete grading scale "${s.name}"?`)) deleteScaleM.mutate(s.id); }}
                        disabled={s.is_default}
                        title={s.is_default ? 'Cannot delete default scale' : 'Delete'}
                        className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded disabled:opacity-30 disabled:cursor-not-allowed">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-1 text-xs">
                    {(s.items ?? []).map((it: any, i: number) => (
                      <div key={i} className="flex items-center gap-1.5 py-1 px-2 bg-slate-50 rounded">
                        <span className="font-bold text-slate-700 w-7">{it.grade}</span>
                        <span className="text-slate-500 text-[10px]">{it.min_score}–{it.max_score}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
           )}
        </div>

        {/* ═══════════════ RESULT COLUMNS ═══════════════ */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Hash className="w-4 h-4 text-slate-500" />
              <h2 className="font-bold text-slate-900">Result columns</h2>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${totalMax === 100 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                {totalMax} / 100
              </span>
            </div>
            <div className="flex gap-1.5">
              <button onClick={openNewClassSet} className="text-xs font-bold border border-slate-300 text-slate-700 px-3 py-1.5 rounded-lg flex items-center gap-1" title="Different columns for some classes"><Users className="w-3 h-3" /> For some classes</button>
              <button onClick={openNewColumn}
                className="text-xs font-bold bg-[#0f172a] text-white px-3 py-1.5 rounded-lg flex items-center gap-1">
                <Plus className="w-3 h-3" /> New
              </button>
            </div>
          </div>
          <div className="px-4 pt-3 flex gap-1.5 overflow-x-auto">
            <button onClick={() => setSetIdx(-1)} className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-semibold border ${setIdx === -1 ? 'bg-[#0f172a] text-white border-[#0f172a]' : 'border-slate-300 text-slate-600'}`}>All classes</button>
            {sets.map((st, i) => (
              <button key={i} onClick={() => setSetIdx(i)} title={st.classes.join(', ')} className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-semibold border ${setIdx === i ? 'bg-[#0f172a] text-white border-[#0f172a]' : 'border-slate-300 text-slate-600'}`}>
                {st.classes.length > 2 ? `${st.classes.slice(0, 2).join(', ')} +${st.classes.length - 2}` : st.classes.join(', ')}
              </button>
            ))}
          </div>
          <p className="px-4 pt-2 text-[11px] text-slate-500">{activeSet ? `Only ${activeSet.classes.join(', ')} use these columns.` : sets.length ? 'Every class without its own columns uses these.' : 'Every class uses these. Use “For some classes” to give a class different columns.'}</p>

          {componentsQ.isLoading ? <p className="p-6 text-center text-sm text-slate-500">Loading…</p> :
           components.length === 0 ? (
            <p className="p-6 text-center text-sm text-slate-500">{activeSet ? 'No columns for these classes.' : 'No result columns yet.'}</p>
           ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase tracking-wider border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left">Column</th>
                  <th className="px-4 py-3 text-left">Code</th>
                  <th className="px-4 py-3 text-right">Max</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {components.map((c: any) => (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-900">{c.name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{c.code}</td>
                    <td className="px-4 py-3 text-right font-bold">{c.max_score}</td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] font-bold uppercase bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{c.kind ?? 'ca'}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => openEditColumn(c)} className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded">
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={async () => { if (await askConfirm(`Delete column "${c.code}"?`)) deleteColumnM.mutate(c.id); }}
                          className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 border-t border-slate-200">
                <tr>
                  <td colSpan={2} className="px-4 py-3 text-xs font-bold text-slate-700 text-right">Total</td>
                  <td className={`px-4 py-3 text-right font-extrabold ${totalMax === 100 ? 'text-green-700' : 'text-amber-700'}`}>{totalMax}</td>
                  <td colSpan={2} className="px-4 py-3 text-xs">
                    {totalMax === 100
                      ? <span className="text-green-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Perfect — sums to 100</span>
                      : totalMax > 100
                      ? <span className="text-red-700 inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Exceeds 100</span>
                      : <span className="text-amber-700 inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Add {100 - totalMax} more</span>}
                  </td>
                </tr>
              </tfoot>
            </table>
           )}
        </div>
      </div>

      {/* ═══════════════ SCALE FORM MODAL ═══════════════ */}
      {scaleForm && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <form onSubmit={validateAndSaveScale} className="bg-white w-full max-w-2xl rounded-2xl shadow-xl my-8">
            <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
              <h3 className="font-bold text-slate-900">{scaleForm.id ? 'Edit grade scale' : 'New grade scale'}</h3>
              <button type="button" onClick={closeScaleForm} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
                </div>
              )}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Name *</label>
                <input value={scaleForm.name} onChange={(e) => setScaleForm({ ...scaleForm, name: e.target.value })}
                  placeholder="e.g. Standard (A1–F9)" required
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <AppliesTo value={scaleForm.class_ids} onChange={(v) => setScaleForm({ ...scaleForm, class_ids: v, is_default: v.length ? false : scaleForm.is_default })} classes={classes}
                note="Classes without their own scale grade with the default scale." />
              {scaleForm.class_ids.length === 0 && (
                <div>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={scaleForm.is_default}
                      onChange={(e) => setScaleForm({ ...scaleForm, is_default: e.target.checked })}
                      className="w-4 h-4" />
                    <span className="text-sm text-slate-700">Make this the default scale (all classes without their own)</span>
                  </label>
                </div>
              )}
              {scaleForm.class_ids.length > 0 && <p className="text-[11px] text-slate-500">A class has one scale: choosing a class here moves it off any other scale.</p>}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-slate-700">Grade bands *</label>
                  <button type="button" onClick={addGradeItem}
                    className="text-xs font-semibold text-[#0f172a] hover:underline inline-flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Add band
                  </button>
                </div>
                <div className="space-y-2">
                  {scaleForm.items.map((it, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-start">
                      <input value={it.grade} onChange={(e) => updateGradeItem(idx, { grade: e.target.value })}
                        placeholder="A1" maxLength={4}
                        className="col-span-2 border border-slate-300 rounded-lg px-2 py-2 text-sm font-mono text-center uppercase" />
                      <input value={it.min_score} onChange={(e) => updateGradeItem(idx, { min_score: e.target.value })}
                        type="number" min={0} max={100} placeholder="Min"
                        className="col-span-2 border border-slate-300 rounded-lg px-2 py-2 text-sm text-center" />
                      <input value={it.max_score} onChange={(e) => updateGradeItem(idx, { max_score: e.target.value })}
                        type="number" min={0} max={100} placeholder="Max"
                        className="col-span-2 border border-slate-300 rounded-lg px-2 py-2 text-sm text-center" />
                      <input value={it.remark} onChange={(e) => updateGradeItem(idx, { remark: e.target.value })}
                        placeholder="Remark (Excellent)" className="col-span-4 border border-slate-300 rounded-lg px-2 py-2 text-sm" />
                      <button type="button" onClick={() => removeGradeItem(idx)}
                        className="col-span-2 p-2 text-red-500 hover:bg-red-50 rounded-lg">
                        <Trash2 className="w-4 h-4 mx-auto" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex gap-3 bg-slate-50 rounded-b-2xl">
              <button type="button" onClick={closeScaleForm}
                className="flex-1 py-2.5 border border-slate-300 rounded-lg text-slate-700 font-semibold text-sm">Cancel</button>
              <button type="submit" disabled={saveScaleM.isPending}
                className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">
                {saveScaleM.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ═══════════════ COLUMN FORM MODAL ═══════════════ */}
      {columnForm && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <form onSubmit={saveColumn} className="bg-white w-full max-w-md rounded-2xl shadow-xl">
            <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
              <h3 className="font-bold text-slate-900">{columnForm.id ? 'Edit column' : forSome ? 'New column for some classes' : 'New column'}</h3>
              <button type="button" onClick={closeColumnForm} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
                </div>
              )}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Name *</label>
                <input value={columnForm.name} onChange={(e) => setColumnForm({ ...columnForm, name: e.target.value })}
                  placeholder="e.g. First Continuous Assessment" required
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Code *</label>
                  <input value={columnForm.code} onChange={(e) => setColumnForm({ ...columnForm, code: e.target.value.toUpperCase() })}
                    placeholder="CA1" maxLength={12} required
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono uppercase" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Max score *</label>
                  <input type="number" value={columnForm.max_score}
                    onChange={(e) => setColumnForm({ ...columnForm, max_score: e.target.value })}
                    placeholder="10" min={1} max={100} required
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Type</label>
                <select value={columnForm.kind}
                  onChange={(e) => setColumnForm({ ...columnForm, kind: e.target.value as any })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                  <option value="ca">CA — Continuous Assessment</option>
                  <option value="test">Test</option>
                  <option value="project">Project</option>
                  <option value="exam">Exam</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <AppliesTo startSome={forSome} value={columnForm.class_ids} onChange={(v) => setColumnForm({ ...columnForm, class_ids: v })} classes={classes}
                note="Used by every class that has no columns of its own." />
              <p className="text-xs text-slate-500">
                The columns of each class must add up to no more than 100. A class with its own columns uses only those — add all its columns (e.g. CA1, CA2, EXAM) for it.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex gap-3 bg-slate-50 rounded-b-2xl">
              <button type="button" onClick={closeColumnForm}
                className="flex-1 py-2.5 border border-slate-300 rounded-lg text-slate-700 font-semibold text-sm">Cancel</button>
              <button type="submit" disabled={saveColumnM.isPending}
                className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">
                {saveColumnM.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
